import * as React from 'react'

import { MobXProviderContext } from 'mobx-react'

import {
  getResourceControlsForRecord,
  isProjectLocationRecord,
  resourceTypeSupportsControls,
} from '~/client/src/shared/components/ResourceControls/controlKeys'
import { ResourceControlsTabProvider } from '~/client/src/shared/components/ResourceControls/ResourceControlsTabProvider'
import { ResourceEdit } from '~/client/src/shared/components/ResourceViewEdit'
import { ResourceEditDetailsBody } from '~/client/src/shared/components/ResourceViewEdit/ResourceEditDetailsBody'
import {
  controlsSection,
  generalResourceSection,
  struxTagTelemetrySections,
  withDefaultExpanded,
} from '~/client/src/shared/components/ResourceViewEdit/resourceEditSectionBuilders'
import { IResourceEditSection } from '~/client/src/shared/components/ResourceViewEdit/ResourceEditSections'
import { IResourceLinkFieldResult } from '~/client/src/shared/hooks/useEquipmentSensorLinkField'
import { IControlFields } from '~/client/src/shared/hooks/useResourceControls'
import { IResourceRuntimeContext } from '~/client/src/shared/resources/definitions'
import type { IResourceControlCommands } from '~/client/src/shared/resources/domain/resourceControls'
import type { ResourceSchema } from '~/client/src/shared/resources/domain/resourceSchema'
import InitialState from '~/client/src/shared/stores/InitialState'

import { ResourceEditRecord } from '../ResourceViewEdit/ResourceEditContext'

interface IProps {
  recordId: string
  record: ResourceEditRecord | null
  templateId?: string
  resourceTypeId: string
  runtimeContext: IResourceRuntimeContext
  projectId?: string
  qrCodeUrl: string | null
  isCreationMode: boolean
  /**
   * The resource's link to another resource. Optional: only equipment links to a
   * StruxTag, and a type without one shows no link row and no telemetry.
   */
  linkField?: IResourceLinkFieldResult
  onClose: () => void
  onSave: (formData: Record<string, any>) => Promise<void>
  onTemplateChange?: (templateId: string | null) => void
  onOpenLinked?: () => void
  formSelector?: React.ReactNode
  formId?: string
  /**
   * Extra sections appended after the standard ones. Used by the maps view to
   * inject an Equipment-only "Map Properties" section (desktop-layer content).
   */
  extraSections?: IResourceEditSection[]
  /**
   * Rendered inside the form in every mode, whatever sections are shown or collapsed. The maps
   * view mirrors the form onto the live map item here, so a drawn-but-unsaved item previews
   * its icon and colour before Create.
   */
  formSync?: React.ReactNode
  /**
   * The resource's saved controls, read off the record's `root`. When provided
   * (and the type supports controls) a Controls section is shown; omit it to
   * hide controls (e.g. the maps card, which focuses on map properties).
   */
  controlFields?: IControlFields
  /**
   * The control commands for THIS resource type. Passed in rather than resolved here:
   * the body is resource-agnostic apart from its name, and naming a store hook meant
   * any other type would write its controls through the equipment store.
   */
  controlCommands: IResourceControlCommands
  /**
   * Root-field seeds (icon/color/parent) for creation mode — e.g. the maps card
   * pre-fills the icon a new equipment was drawn with. Ignored when editing.
   */
  creationRootDefaults?: Record<string, any>
  /** Rendered inside Maps — see ResourceEdit. */
  isMapsSurface?: boolean
  /** Keep the card open after a create (the maps card stays open in edit mode). */
  keepOpenOnCreate?: boolean
  /** Delete the resource; shows the header's 3-dot overflow menu when provided. */
  onDelete?: () => void
  /**
   * Which section opens expanded by default (edit mode): `general` from the
   * objects table, `controls` from the controls table, `map` from the maps view.
   * Creation mode always expands `general` (the only visible section).
   */
  defaultExpandedSectionId?: string
  /**
   * Supplied by desktop panels so the inherited-hours chip's "Project Settings"
   * link navigates; omitted on mobile (link stays inactive).
   */
  onEditProjectHours?: () => void
  resourceSchema: ResourceSchema
}

export const EquipmentResourceEditBody: React.FC<IProps> = ({
  recordId,
  record,
  templateId,
  resourceTypeId,
  runtimeContext,
  projectId,
  qrCodeUrl,
  isCreationMode,
  linkField,
  onClose,
  onSave,
  onTemplateChange,
  onOpenLinked,
  formSelector,
  formId,
  extraSections = [],
  formSync,
  controlFields,
  controlCommands,
  creationRootDefaults,
  isMapsSurface,
  keepOpenOnCreate,
  onDelete,
  defaultExpandedSectionId = 'general',
  onEditProjectHours,
  resourceSchema,
}) => {
  const showControls =
    !!controlFields && resourceTypeSupportsControls(resourceTypeId)

  // The link to a StruxTag and the Utilization + Sessions telemetry sections are
  // the card's StruxTag surfaces — hide them when the app is off for the project.
  const { state } = React.useContext(MobXProviderContext) as {
    state?: InitialState
  }
  const isStruxTagsEnabled = !state?.isStruxTagsDisabled

  const detailsBody = (
    <ResourceEditDetailsBody
      isCreationMode={isCreationMode}
      linkField={linkField}
      onOpenLinked={onOpenLinked}
      formSelector={formSelector}
      showLink={isStruxTagsEnabled && !!linkField}
      sections={withDefaultExpanded(
        [
          generalResourceSection(),
          ...(showControls ? [controlsSection()] : []),
          ...extraSections,
          // Utilization + Sessions (StruxTag telemetry) come last, after Map.
          // Telemetry describes the linked StruxTag, so a type with no link has none.
          ...(isStruxTagsEnabled && linkField
            ? struxTagTelemetrySections({
                projectId,
                infuseId: linkField.linkRootProps.linkedItem?.infuseId,
              })
            : []),
        ],
        defaultExpandedSectionId,
      )}
    />
  )

  const card = (
    <ResourceEdit.Root
      recordId={recordId}
      record={record}
      templateId={templateId}
      resourceTypeId={resourceTypeId}
      runtimeContext={runtimeContext}
      formId={formId}
      onClose={onClose}
      onSave={onSave}
      onTemplateChange={onTemplateChange}
      creationRootDefaults={creationRootDefaults}
      isMapsSurface={isMapsSurface}
      keepOpenOnCreate={keepOpenOnCreate}
      onDelete={onDelete}
      qrCodeUrl={qrCodeUrl}
      resourceSchema={resourceSchema}
    >
      {formSync}
      {detailsBody}
    </ResourceEdit.Root>
  )

  // The provider wraps Root so the card's single footer Save (in Root) can also
  // commit the controls draft — one Save/Cancel for the whole card.
  return showControls && controlFields ? (
    <ResourceControlsTabProvider
      recordId={recordId}
      record={record}
      resourceTypeId={resourceTypeId}
      projectId={projectId || ''}
      controls={getResourceControlsForRecord(resourceTypeId, record)}
      isProjectLocation={isProjectLocationRecord(resourceTypeId, record)}
      controlCommands={controlCommands}
      controlFields={controlFields}
      onClose={onClose}
      onEditProjectHours={onEditProjectHours}
    >
      {card}
    </ResourceControlsTabProvider>
  ) : (
    card
  )
}
